import { Star } from "@phosphor-icons/react"
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { SearchField } from "@tokenterminal/core/ui/input/SearchField"

import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import HeaderWithSubtitle from "@tokenterminal/core/ui/table/HeaderWithSubtitleExperimental"
import SortableHeader from "@tokenterminal/core/ui/table/SortableHeaderExperimental"
import { Table } from "@tokenterminal/core/ui/table/Table"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { useIsMobile } from "@tokenterminal/ui/hooks/isMobile"
import { useTimeseries } from "modules/projects/hooks/useTimeseries"
import { formatNumber } from "modules/screener/utils/cellFormatHelper"
import Link from "next/link"
import { useRouter } from "next/router"
import { useEffect, useMemo, useState } from "react"
import { type MetricConfiguration } from "utils/trpc"
import { iconsBySlug } from "../constants"
import { getBadgeColor } from "./colorUtils"
import {
  useMarketSectorsWithCountAndMetrics,
  type MarketSectorWithCountAndMetrics,
} from "./hooks/useMarketSectorsWithCountAndMetrics"
import { useTopProjectsBySectorSlug } from "./hooks/useTopProjectsBySectorSlug"
import MetricTooltip from "./MetricTooltip"
import { SparkAreaChart } from "./SparkChart"
const useSparklineData = (metricId: string) => {
  const { data: sparklineData } = useTimeseries({
    metricIds: [metricId],
    interval: "7d",
    groupBy: "market-sector",
    dataIds: [],
  })

  return useMemo(
    () =>
      sparklineData?.data.reduce((acc: Record<string, number[]>, curr) => {
        if (!curr.market_sector) return acc

        acc[curr.market_sector] = [
          ...(acc[curr.market_sector] || []),
          curr.value || 0,
        ]
        return acc
      }, {}),
    [sparklineData]
  )
}

const TopProjectsButtonModal = ({
  topProjects,
}: {
  topProjects: {
    project: {
      project_id: string
      name: string
      logo: string
    }
  }[]
}) => {
  return (
    <>
      {topProjects.slice(0, 3).map((project, index) => (
        <Tooltip
          key={project.project.project_id}
          content={project.project.name}
        >
          <Tooltip.Trigger>
            <Link
              href={`/projects/${project.project.project_id}`}
              className="flex gap-2 -ml-1"
              tabIndex={0}
            >
              <div
                key={project.project.project_id}
                className="hover:bg-neutral-200 dark:hover:bg-neutral-800 rounded-lg p-1.5"
              >
                <Avatar
                  src={`https://static1.tokenterminal.com/${project.project.logo}`}
                  className="rounded-full overflow-hidden w-[18px] h-[18px] flex-shrink-0"
                />
              </div>
            </Link>
          </Tooltip.Trigger>
        </Tooltip>
      ))}
    </>
  )
}

export const MarketSectorsTable = ({
  metricConfiguration,
  interval,
}: {
  metricConfiguration: MetricConfiguration
  interval: Interval
}) => {
  const selectedMetricId = metricConfiguration?.metric_id
  const isMobile = useIsMobile()
  const router = useRouter()

  // State
  const [searchFilter, setSearchFilter] = useState("")
  const [sorting, setSorting] = useState([
    {
      id: selectedMetricId,
      desc: true,
    },
  ])

  // Fetch data
  const sparkline = useSparklineData(selectedMetricId)
  const projectsBySectorSlug = useTopProjectsBySectorSlug(selectedMetricId)
  const marketSectorsListData = useMarketSectorsWithCountAndMetrics(
    selectedMetricId,
    interval
  )

  // Table setup
  const columns: ColumnDef<MarketSectorWithCountAndMetrics>[] = useMemo(() => {
    const result: ColumnDef<MarketSectorWithCountAndMetrics>[] = [
      {
        id: "name",
        header: ({ column }) => (
          <SortableHeader
            isActiveSort={!!column.getIsSorted()}
            sortDirection={column.getIsSorted() || null}
            onPress={() => column.toggleSorting()}
            title="Market sector"
            subtitle="Name"
          />
        ),
        cell: ({ row, table }) => {
          const sector = row.original
          const currentIndex = table
            .getSortedRowModel()
            .rows.findIndex((row) => row.original.slug === sector.slug)

          return (
            <Link
              href={`/markets/${sector.slug}`}
              className="flex gap-3 items-center py-1"
            >
              <div
                className={
                  "text-sm w-4 h-4 flex items-center justify-center " +
                  "text-fg-secondary flex-shrink-0"
                }
              >
                {currentIndex + 1}
              </div>
              <div className="bg-neutral-300/30 dark:bg-neutral-700/50 rounded-lg w-6 h-6 flex items-center justify-center flex-shrink-0">
                <Icon as={iconsBySlug[sector.slug] || Star} />
              </div>
              <span className="font-medium whitespace-nowrap">
                {sector.name}
              </span>
            </Link>
          )
        },
        accessorFn: (row) => row.name,
        enableColumnFilter: true,
      },
      {
        id: "projects",
        header: ({ column }) => (
          <div className="flex justify-end pr-20">
            <SortableHeader
              isActiveSort={!!column.getIsSorted()}
              sortDirection={column.getIsSorted() || null}
              onPress={() => column.toggleSorting()}
              title="Projects"
              subtitle="Count"
              alignment="right"
            />
          </div>
        ),
        cell: ({ row }) => {
          const sector = row.original
          return (
            <div
              className="text-right text-[13.5px] pr-20"
              style={{ fontFamily: "var(--font-geist-mono)" }}
            >
              {sector.projects_count || 0}
            </div>
          )
        },
        accessorFn: (row) => row.projects_count || 0,
        // size: 100,
        // minSize: 80,
        // maxSize: 150,
      },
      {
        id: "top_projects",
        header: () => (
          <div className="flex justify-start">
            <HeaderWithSubtitle
              title="Top 3 projects"
              subtitle={`By ${metricConfiguration?.title}`}
            />
          </div>
        ),
        cell: ({ row }) => {
          const sector = row.original
          const topProjects = projectsBySectorSlug[sector.slug] || []

          return (
            <div className="flex justify-start">
              {/* Hover */}
              {true && <TopProjectsButtonModal topProjects={topProjects} />}
              {/* Links */}
              {false && (
                <div className="flex gap-1 -mx-1">
                  {topProjects.slice(0, 1).map((project, index) => (
                    <Tooltip key={index} content={project.project.name}>
                      <Tooltip.Trigger>
                        <Link
                          href={`/projects/${project.project.project_id}`}
                          className="relative flex gap-1.5 items-center py-1 px-1.5 hover:underline"
                          style={{
                            textUnderlineOffset: "3px",
                          }}
                        >
                          <Avatar
                            src={`https://static1.tokenterminal.com/${project.project.logo}`}
                            className="rounded-full overflow-hidden w-4 h-4 flex-shrink-0"
                          />
                          {index !== 80 && (
                            <span className="text-sm text-fg-secondary">
                              {project.project.name}
                            </span>
                          )}
                        </Link>
                      </Tooltip.Trigger>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          )
        },
        // size: 310,
      },
      {
        id: selectedMetricId,
        header: ({ column }) => (
          <div className="flex justify-end">
            <SortableHeader
              isActiveSort={!!column.getIsSorted()}
              sortDirection={column.getIsSorted() || null}
              onPress={() => column.toggleSorting()}
              title={
                <MetricTooltip metricId={selectedMetricId}>
                  {metricConfiguration?.title}
                </MetricTooltip>
              }
              subtitle={
                metricConfiguration?.static ? "Latest" : `${interval} sum`
              }
              alignment="right"
            />
          </div>
        ),
        cell: ({ row }) => {
          const sector = row.original
          return (
            <div
              className="text-right text-[13.5px]"
              style={{ fontFamily: "var(--font-geist-mono)" }}
            >
              {formatNumber(
                sector.metrics[selectedMetricId]?.value || 0,
                "compact",
                metricConfiguration?.format === "price" ? "$" : undefined
              ).toUpperCase()}
            </div>
          )
        },
        accessorFn: (row) => row.metrics[selectedMetricId]?.value || 0,
        // size: metricConfiguration?.title?.length || 0 * 20,
        // minSize: 100,
        // maxSize: 200,
      },
      {
        id: `${selectedMetricId}_change`,
        header: ({ column }) => (
          <div className="flex justify-start">
            <SortableHeader
              isActiveSort={!!column.getIsSorted()}
              sortDirection={column.getIsSorted() || null}
              onPress={() => column.toggleSorting()}
              title="Change"
              subtitle={interval}
            />
          </div>
        ),
        cell: ({ row }) => {
          const sector = row.original
          const sparkArray = sparkline?.[sector.slug] || []
          const change = sector.metrics[selectedMetricId]?.change || 0

          const changePercent = Math.abs(
            (sector.metrics[selectedMetricId]?.change || 0) * 100
          ).toFixed(2)

          const chartData = sparkArray.map((value, index) => ({
            month: index,
            Performance: value,
          }))
          const color = change >= 0 ? "green" : "red"
          const badgeColor = getBadgeColor(change || 0)
          // limit to 1 decimal places
          const metricChange = Math.abs(Math.round(change * 10) / 10) || 0
          const badgeColorClass =
            badgeColor === "red"
              ? "text-red-600 dark:text-red-400"
              : badgeColor === "green"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-neutral-600 dark:text-neutral-400"
          return (
            <div className="flex justify-start gap-2">
              <div
                className={`!py-0 !gap-0 tabular-nums tracking-wider ${badgeColorClass} tabular-nums !font-normal`}
              >
                {change > 0 && <span className="mr-px">+</span>}
                {change < 0 && <span className="mr-px">-</span>}
                {changePercent}%
              </div>
              <SparkAreaChart
                data={chartData}
                categories={["Performance"]}
                index={"month"}
                colors={[color]}
                className="h-5 w-full hidden md:block"
                fill="none"
              />
            </div>
          )
        },
        accessorFn: (row) => row.metrics[selectedMetricId]?.change || 0,
        // size: 100,
      },
      {
        id: `${selectedMetricId}_percent_of_total`,
        header: ({ column }) => (
          <div className="flex justify-end">
            <SortableHeader
              isActiveSort={!!column.getIsSorted()}
              sortDirection={column.getIsSorted() || null}
              onPress={() => column.toggleSorting()}
              title="Market share"
              alignment="right"
              subtitle={interval}
            />
          </div>
        ),
        cell: ({ row }) => {
          const sector = row.original

          return (
            <div
              className="text-right text-[13.5px]"
              style={{
                fontFamily: "var(--font-geist-mono)",
              }}
            >
              {sector.percentOfTotal !== null ? (
                <>{(sector.percentOfTotal * 100).toFixed(2)}%</>
              ) : (
                "-"
              )}
            </div>
          )
        },
        accessorFn: (row) => row.percentOfTotal || 0,
        // size: 100,
      },
    ]

    if (isMobile) {
      return result.filter(
        (column) => column.id && ["name", selectedMetricId].includes(column.id)
      )
    }

    return result
  }, [
    selectedMetricId,
    isMobile,
    metricConfiguration?.title,
    metricConfiguration?.static,
    metricConfiguration?.format,
    projectsBySectorSlug,
    interval,
    sparkline,
  ])

  const table = useReactTable({
    data: marketSectorsListData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      globalFilter: searchFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearchFilter,
    enableColumnResizing: true,
    debugTable: true,
    enableSortingRemoval: false,
  })

  useEffect(() => {
    setSorting([
      {
        id: selectedMetricId,
        desc: true,
      },
    ])
  }, [selectedMetricId])

  return (
    <div className="flex flex-col w-full">
      <div className="mb-4 flex items-center gap-2">
        <SearchField
          placeholder="Search sectors..."
          value={searchFilter}
          onChange={setSearchFilter}
          className="w-full"
          aria-label="Search sectors"
        />
      </div>
      <div className="overflow-x-auto self-stretch w-[calc(100%+1rem)] px-2 -ml-2">
        <Table density="large" className="w-full table-fixed" rowHover>
          <Table.Header>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id} style={{ clipPath: "none" }}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Table.HeaderCell
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className={`first:pl-0 last:pr-0`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeaderCell>
                  )
                })}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row
                key={row.id}
                onClick={() => router.push(`/markets/${row.original.slug}`)}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <Table.Cell
                      key={cell.id}
                      className={`first:pl-0 last:pr-0`}
                      style={{ width: `${cell.column.getSize()}px` }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  )
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      {table.getRowModel().rows.length === 0 && (
        <div className="text-center py-4 text-fg-secondary">
          No sectors found matching your search
        </div>
      )}
    </div>
  )
}
